import React, { useEffect, useRef, useState } from 'react';
import styles from './HomePageQuickLinks.module.scss';
import { useSelector } from 'react-redux';
import Slider from 'react-slick';
import ArrowIcon from '@/components/Logos/Arrow';
import { selectSliderElementsGen } from '@/helpers/generalHelper';
import Srcset from '@/components/common/Srcset';
import useMediaQuery from '@/helpers/hooks/useMediaQuery';

function NextArrow(props) {
  const { className, onClick,lightColor } = props;
  return (
      <div className={className} onClick={onClick} tabIndex={0}>
      <Srcset
          requirePath={true}
          svgFile={true}
          src={"images/Icons/Arrow/Right/Large_dark.svg"}
          alt="Arrow Button"
          height="12"
          width="12"
          lightColor={lightColor}
      />
      </div>
  );
}

function PrevArrow(props) {
  const { className, onClick, lightColor } = props;
  return (
      <div className={className} onClick={onClick} tabIndex={0}>
      <Srcset
          requirePath={true}
          svgFile={true}
          src={"images/Icons/Arrow/Right/Large_dark.svg"}
          alt="Arrow Button"
          height="12"
          width="12"
          lightColor={lightColor}
      />
      </div>
  );
}

const HomePageQuickLinks = ({ title, links, backgroundColor, backgroundImage, deviceTypes, targetApp, whiteText }) => {
  const isApp = useSelector((state) => state.pwa.is_PWA);  
  const isDesktop = useMediaQuery("(min-width: 1025px)");
  const isTablet = useMediaQuery("(min-width: 601px) and (max-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 600px)");
  const sliderRef= useRef(null); 
  const [tabPressed, setTabPressed] = useState(false);
  
  useEffect(()=>{
      if(!isDesktop){
        return;
      }
      // to handle tabindexing
      const handleKeyDown = (event) =>{
        if(event.key === "Tab" && !tabPressed){
          setTabPressed(true);
          selectSliderElementsGen(sliderRef);
        } 
      };
      const handleKeyUp = (event) =>{
        if(event.key === "Tab" ){
          setTabPressed(false);
        }
      };
  
      window.addEventListener("keydown",handleKeyDown);
      window.addEventListener("keyup",handleKeyUp);
  
      return ()=>{
        window.removeEventListener("keydown",handleKeyDown);
        window.removeEventListener("keyup",handleKeyUp);
      }
  },[sliderRef,tabPressed,isDesktop]);
  

  let settings = {
    accessibility:true,
    speed: 500,
    slidesToShow: Math.min(links?.length,7),
    slidesToScroll: 7, 
    infinite: links?.length > 7,
    arrows: true,
    autoplay: false,
    lazyLoad: true,
    draggable: false,
    nextArrow: <NextArrow lightColor={whiteText}/>,
    prevArrow: <PrevArrow lightColor={whiteText}/>,
    responsive: [
    {
        breakpoint: 1025,
        settings: {
        slidesToShow: 7,
        slidesToScroll: 7,
        arrows: false,
        autoplay: false,
        draggable: true,
        },
    }]
  };

  const hasAllImages = links?.every(link => link.productImage?.data?.images?.[0]?.src);

  const slides = links?.map((link, index) => {
    return (
      <a key={index} tabIndex={0} href={link.url} className={`${styles.quickLink} ga-home-quick-links ${!hasAllImages && styles.noImageWrapper}`} >
        {hasAllImages&&<img 
          src={link.productImage?.data?.images?.[0]?.src+"&width=240"} 
          className={`${styles.productImage} ga-home-quick-links`} 
          alt={`Image of ${link.displayText}`}
        />}
        <span className={`x-small-md x-small-sm x-small-xs ga-home-quick-links`}>{link.displayText}</span>
      </a>
    );
  });

  const mobileSlides = links?.map((link, index) => {
    return (  
        <a href={link.url} key={index} className={`${styles.quickLink} ga-home-quick-links ${!hasAllImages && styles.noImageWrapper}`}> 
          {hasAllImages && <span><img src={link.productImage?.data?.images?.[0]?.src+"&height=70"} className={`${styles.productImage} ga-home-quick-links`}  alt={`Image of ${link.displayText}`}
          /></span>}
          <span className={`small-xs ga-home-quick-links ${styles.ql_text} ga-home-quick-links ${!hasAllImages && styles.noImage}`}>{link.displayText}</span>
          <ArrowIcon className={`${styles.arrowIcon} ga-home-quick-links`} height="10" width="15" stroke={`${whiteText? "#ffffff" : "#2f2f30"}`}/>
        </a> 
     )
  })

  // Function to check if the component should be displayed based on device type
  const shouldDisplay = () => {
    if (targetApp && isApp) { return true}
    if (deviceTypes && deviceTypes.length > 0) {
      return deviceTypes.some(element => {  
        if (isDesktop && element.deviceType === 'desktop') return true;
        if (isTablet && element.deviceType === 'tablet') return true;
        if (isMobile && element.deviceType === 'mobile' && !isApp) return true;
      });
    }
    return false;
  };

  if (!shouldDisplay()) return null;

  return (
    <div className={`${styles.homePageQuickLinks} ${whiteText ? styles.colorLight:""} ${!hasAllImages ? styles.onlyText:""}` } style={{...(backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : {}), backgroundColor}}>
      <h3 className={`heading-350 ${styles.title}`}>{title}</h3>
      {isDesktop || isTablet ? <Slider ref={sliderRef} aria-live="polite" role="region" lazyLoad="ondemand" {...settings}>
        {slides}
      </Slider> : 
       <> {mobileSlides}</>
      }
    </div>
  );
};

export default HomePageQuickLinks;